/**
 * O que conferir no portal (E172.F7.T03): o endereço do material, o código, o domínio, o envelope
 * e as peças abrangidas. O endereço abre numa aba nova. É a pessoa que consulta; a tela só mostra
 * o que o material traz e o que merece atenção antes de registrar.
 */
import { MonoData } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { hashAbreviado, type AlvoDaConferenciaData } from '@/lib/conferenciaNoPortal';

const t = ptBR.conferenciaNoPortal.alvo;

function Aviso({ texto, testId }: { texto: string; testId: string }): JSX.Element {
  return (
    <p className="text-body-sm text-warning-700" data-testid={testId}>
      {texto}
    </p>
  );
}

function Endereco({ alvo }: { alvo: AlvoDaConferenciaData }): JSX.Element | null {
  if (alvo.url === null || alvo.origem_do_endereco === null) return null;
  return (
    <div className="space-y-1">
      <dt className="text-caption text-fg-dim">{t.endereco}</dt>
      <dd className="break-all text-body-sm">
        <a
          href={alvo.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-accent-700 underline"
          data-testid="portal-endereco"
        >
          {alvo.url}
        </a>{' '}
        <span className="text-fg-dim">({t.origem[alvo.origem_do_endereco]})</span>
      </dd>
      {alvo.https ? null : <Aviso texto={t.semConexaoSegura} testId="portal-sem-https" />}
      {alvo.host ? (
        <dd className="text-body-sm text-fg-muted">
          {t.dominio}: <MonoData>{alvo.host}</MonoData>
        </dd>
      ) : null}
    </div>
  );
}

function Codigo({ alvo }: { alvo: AlvoDaConferenciaData }): JSX.Element | null {
  if (alvo.codigo === null) return null;
  return (
    <div className="space-y-1">
      <dt className="text-caption text-fg-dim">{t.codigo}</dt>
      <dd className="text-body-sm" data-testid="portal-codigo">
        <MonoData>{alvo.codigo}</MonoData>
      </dd>
      {alvo.url === null ? null : alvo.codigo_no_endereco ? (
        <p className="text-caption text-fg-muted">{t.codigoNoEndereco}</p>
      ) : (
        <Aviso texto={t.codigoForaDoEndereco} testId="portal-codigo-fora" />
      )}
    </div>
  );
}

function Documentos({ alvo }: { alvo: AlvoDaConferenciaData }): JSX.Element {
  return (
    <div className="space-y-1">
      <dt className="text-caption text-fg-dim">{t.documentos}</dt>
      {alvo.documentos.length === 0 ? (
        <dd className="text-body-sm text-fg-muted">{t.semDocumentos}</dd>
      ) : (
        alvo.documentos.map((d) => (
          <dd
            key={d.documento_id}
            className="text-body-sm"
            data-testid="portal-documento"
            title={d.sha256}
          >
            {d.nome} · <MonoData>{hashAbreviado(d.sha256)}</MonoData>
          </dd>
        ))
      )}
    </div>
  );
}

export function AlvoDoPortal({ alvo }: { alvo: AlvoDaConferenciaData }): JSX.Element {
  return (
    <section
      className="space-y-sc-2"
      data-testid="portal-alvo"
      data-situacao={alvo.situacao}
      aria-labelledby="portal-alvo-titulo"
    >
      <h4 id="portal-alvo-titulo" className="text-body text-fg">
        {t.titulo}
      </h4>
      <p className="text-body-sm text-fg-muted" data-testid="portal-situacao">
        {t.situacao[alvo.situacao]}
      </p>
      <dl className="space-y-sc-2">
        <Endereco alvo={alvo} />
        <Codigo alvo={alvo} />
        {alvo.host_dos_codigos_lidos === 'diverge' ? (
          <Aviso texto={t.codigosLidos.diverge} testId="portal-codigos-divergem" />
        ) : null}
        {alvo.plataforma ? (
          <div>
            <dt className="text-caption text-fg-dim">{t.plataforma}</dt>
            <dd className="text-body-sm">{alvo.plataforma}</dd>
          </div>
        ) : null}
        {alvo.envelope_id ? (
          <div>
            <dt className="text-caption text-fg-dim">{t.envelope}</dt>
            <dd className="break-all text-body-sm">
              <MonoData>{alvo.envelope_id}</MonoData>
            </dd>
          </div>
        ) : null}
        <Documentos alvo={alvo} />
      </dl>
    </section>
  );
}
